import React, { useRef, useEffect } from 'react'

import MediaIcon from '../MediaIcon'

import RightArrow from 'public/assets/svg/right_arrow.svg'
import ModalClose from 'public/assets/svg/modal_close.svg'

import { motion } from 'framer-motion'
import styles from './styles'

import injectSheet from 'react-jss'
import compose from 'recompose/compose'
import InstagramThumbnail from 'aria-viewer/tour/components/InstagramThumbnail'
import CampusreelThumbnail from '../buttons/supplementalmedia/campusreelThumbnail'
import { MEDIA_TYPES, NETWORK_TYPES } from 'aria-viewer/tour/constants'

const getOneTrustSafeImgSrc = imgSrc => {
    return typeof imgSrc === 'string' ? imgSrc.replace('img.youtube.com', 'i.ytimg.com') : imgSrc
}

const MediaViewer = props => {
    const {
        media,
        setIdx,
        selectedIdx,
        children,
        showMediaViewer,
        hideSupplementalMediaView,
        supplementalMediaVisible,
        errorIdx,
        setErrorIdx,
        classes,
    } = props

    const sceneAnimations = {
        open: {
            y: '0',
            opacity: 1,
            transition: {
                ease: [0.28, 0.57, 0.2, 1],
                duration: 0.5,
                delay: 0.5,
            },
        },
        closed: {
            y: '-100px',
            opacity: 0,
            transition: {
                ease: 'easeIn',
                duration: 0.25,
            },
        },
        openBG: {
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
        closeBG: {
            opacity: 0,
            transition: {
                duration: 0.5,
                delay: 0.25,
                onComplete: () => {
                    showMediaViewer(null)
                },
            },
        },
    }

    const carouselRef = useRef(null)

    useEffect(() => {
        const keyDownListener = e => {
            if (e.keyCode === 27) {
                setTimeout(() => {
                    setIdx(0)
                    hideSupplementalMediaView()
                }, 300)
            }
        }
        document.addEventListener('keydown', keyDownListener)
        return () => document.removeEventListener('keydown', keyDownListener)
    }, [])

    return (
        <>
            <motion.div
                initial={{ y: '100px', opacity: 0 }}
                animate={supplementalMediaVisible ? 'open' : 'closed'}
                variants={sceneAnimations}
                className={classes.background}
                data-testid="background"
            >
                <div className={classes.container} data-testid="container">
                    <button
                        type="button"
                        aria-label="Close"
                        className={classes.modalClose}
                        onClick={() =>
                            setTimeout(() => {
                                setIdx(0)
                                hideSupplementalMediaView()
                            }, 300)
                        }
                        data-testid="closeButton"
                    >
                        <img src={ModalClose} />
                    </button>

                    {children}
                    <div className={classes.carousel} ref={carouselRef} data-testid="carousel">
                        {media.map((obj, idx) => {
                            return (
                                <div key={obj.id} className={classes.imgContainer} data-testid="carouselImages">
                                    <button
                                        type="button"
                                        onClick={() => setIdx(idx)}
                                        className={classes.carouselImgButton}
                                    >
                                        {(network => {
                                            switch (network) {
                                                case NETWORK_TYPES.instagram:
                                                    return (
                                                        <InstagramThumbnail
                                                            embedId={
                                                                obj.mediaType === MEDIA_TYPES.photo
                                                                    ? obj.url
                                                                    : obj.videoid
                                                            }
                                                            width={168}
                                                            height={100}
                                                            top={-54}
                                                            left={obj.mediaType === MEDIA_TYPES.photo ? 0 : -60}
                                                            iframeHeight={'160%'}
                                                            iframeWidth={
                                                                obj.mediaType === MEDIA_TYPES.photo ? '100%' : 280
                                                            }
                                                            className={`${classes.img} ${idx === selectedIdx &&
                                                                classes.mediaHighlight}`}
                                                        />
                                                    )
                                                default:
                                                    if (obj.type === 'campusreel') {
                                                        return (
                                                            <CampusreelThumbnail
                                                                embedId={obj.videoid}
                                                                width={168}
                                                                height={100}
                                                                top={-30}
                                                                left={-65}
                                                                iframeHeight={'170%'}
                                                                iframeWidth={'180%'}
                                                                className={`${idx === selectedIdx &&
                                                                    classes.mediaHighlight}`}
                                                            />
                                                        )
                                                    } else {
                                                        return (
                                                            <img
                                                                src={getOneTrustSafeImgSrc(
                                                                    obj.urls.thumb || obj.urls.tiny
                                                                )}
                                                                alt={obj.title}
                                                                className={`${classes.img} ${idx === selectedIdx &&
                                                                    classes.mediaHighlight}`}
                                                                onError={e => {
                                                                    setErrorIdx([...errorIdx, obj.id])
                                                                }}
                                                            />
                                                        )
                                                    }
                                            }
                                        })(obj.network)}
                                    </button>
                                    <div className={classes.thumbnailIcon} data-testid="thumbnailImages">
                                        <MediaIcon
                                            mediaType={obj.mediaType}
                                            network={obj.network}
                                            showBackground={false}
                                            type={obj.type}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                        {media.length > 7 && (
                            <>
                                <button
                                    type="button"
                                    aria-label="View previous media"
                                    className={classes.leftArrow}
                                    onClick={() => {
                                        if (selectedIdx !== 0) {
                                            setIdx(selectedIdx - 1)
                                            carouselRef.current.scrollBy(-160, 0)
                                        }
                                    }}
                                >
                                    <img src={RightArrow} />
                                </button>
                                <button
                                    type="button"
                                    aria-label="View next media"
                                    className={classes.rightArrow}
                                    onClick={() => {
                                        if (selectedIdx !== media.length - 1) {
                                            setIdx(selectedIdx + 1)
                                            carouselRef.current.scrollBy(160, 0)
                                        }
                                    }}
                                >
                                    <img src={RightArrow} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={supplementalMediaVisible ? 'openBG' : 'closeBG'}
                variants={sceneAnimations}
                className={classes.darkBackground}
            />
        </>
    )
}

MediaViewer.defaultProps = {}

export default compose(injectSheet(styles))(MediaViewer)
